import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  Alert,
  TextField,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Business as BusinessIcon,
  Folder as FolderIcon,
  CheckCircle as CheckIcon,
  PlayArrow as PlayIcon,
  Done as DoneIcon
} from '@mui/icons-material';

const ProjectSetup2Page: React.FC = () => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [client, setClient] = useState('');
  const [project, setProject] = useState('');
  const [accountManager, setAccountManager] = useState('');
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [projectCreated, setProjectCreated] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [currentStep, setCurrentStep] = useState<number | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [allStepsComplete, setAllStepsComplete] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = 'Project Setup';
    return () => {
      document.title = 'Start Page'; // Reset to default when leaving
    };
  }, []);

  const clients = [
    'Think Beyond Innovations Inc.',
    'Jason Wright'
  ];

  const setupSteps = [
    {
      id: 1,
      title: 'Update Job Book',
      icon: <BusinessIcon sx={{ fontSize: 24 }} />,
      color: '#9c27b0'
    },
    {
      id: 2,
      title: 'Create ClickTime Project',
      icon: <BusinessIcon sx={{ fontSize: 24 }} />,
      color: '#1976d2'
    },
    {
      id: 3,
      title: 'Create BaseCamp Project',
      icon: <BusinessIcon sx={{ fontSize: 24 }} />,
      color: '#2e7d32'
    },
    {
      id: 4,
      title: 'Create Fileserver Folders',
      icon: <FolderIcon sx={{ fontSize: 24 }} />,
      color: '#ed6c02'
    }
  ];

  const handleCreateProject = async () => {
    setIsCreatingProject(true);
    setProjectCreated(true);
    
    // Auto-execute all steps sequentially
    for (let i = 0; i < setupSteps.length; i++) {
      const step = setupSteps[i];
      setCurrentStep(step.id);
      
      // Simulate step execution (3 seconds each)
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      setCompletedSteps(prev => [...prev, step.id]);
      setCurrentStep(null);
      
      // Small pause between steps
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    // All steps completed
    setAllStepsComplete(true);
    setIsCreatingProject(false);
    setShowSuccess(true);
    
    console.log('Project created with:', { date, client, project, accountManager });
  };

  const handleStepClick = async (stepId: number) => {
    if (completedSteps.includes(stepId) || currentStep !== null) return;
    
    setCurrentStep(stepId);
    
    // Simulate step execution
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    setCompletedSteps(prev => {
      const newCompleted = [...prev, stepId];
      if (newCompleted.length === setupSteps.length) {
        setAllStepsComplete(true);
      }
      return newCompleted;
    });
    setCurrentStep(null);
    
    console.log('Step completed:', stepId);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom 
          sx={{ 
            textAlign: 'center', 
            mb: 4,
            fontWeight: 600,
            color: 'primary.main'
          }}
        >
          Project Setup
        </Typography>

        {/* Form Fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
          {/* Date Field */}
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            fullWidth
            size="medium"
            InputLabelProps={{
              shrink: true
            }}
          />

          {/* Client Dropdown */}
          <FormControl fullWidth size="medium">
            <InputLabel>Client</InputLabel>
            <Select
              value={client}
              label="Client"
              onChange={(e) => setClient(e.target.value)}
            >
              {clients.map((clientOption) => (
                <MenuItem 
                  key={clientOption} 
                  value={clientOption}
                >
                  {clientOption}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Project Text Field */}
          <TextField
            label="Project"
            value={project}
            onChange={(e) => setProject(e.target.value)}
            fullWidth
            size="medium"
            InputLabelProps={{
              sx: { fontSize: '1.2rem' }
            }}
            sx={{
              '& .MuiInputBase-root': {
                minHeight: 60,
                fontSize: '1.2rem'
              }
            }}
          />

          {/* Account Manager Text Field */}
          <TextField
            label="Account Manager"
            value={accountManager}
            onChange={(e) => setAccountManager(e.target.value)}
            fullWidth
            size="medium"
            InputLabelProps={{
              sx: { fontSize: '1.2rem' }
            }}
            sx={{
              '& .MuiInputBase-root': {
                minHeight: 60,
                fontSize: '1.2rem'
              }
            }}
          />
        </Box>

        {/* Create Project Button */}
        <Box sx={{ mb: 4 }}>
          <Button
            variant="contained"
            size="medium"
            fullWidth
            onClick={handleCreateProject}
            disabled={!client || !project || !accountManager || isCreatingProject}
            sx={{
              py: 2,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              boxShadow: 3,
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease'
            }}
            startIcon={isCreatingProject ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isCreatingProject ? 'Creating Project...' : projectCreated ? 'Project Created!' : 'Create Project'}
          </Button>
        </Box>

        {/* Setup Steps */}
        <Typography 
          variant="h5" 
          component="h2" 
          gutterBottom 
          sx={{ 
            mb: 3,
            fontWeight: 500,
            textAlign: 'center'
          }}
        >
          Setup Steps
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
          {setupSteps.map((step) => (
            <Card 
              key={step.id}
              elevation={3}
              sx={{
                cursor: completedSteps.includes(step.id) || currentStep !== null ? 'default' : 'pointer',
                transition: 'all 0.3s ease',
                opacity: !projectCreated ? 0.6 : 1,
                bgcolor: completedSteps.includes(step.id) ? `${step.color}20` : 'background.paper',
                border: completedSteps.includes(step.id) ? `2px solid ${step.color}` : '1px solid transparent',
                '&:hover': !completedSteps.includes(step.id) && currentStep === null && projectCreated ? {
                  transform: 'translateY(-4px)',
                  boxShadow: 8,
                  bgcolor: `${step.color}08`
                } : {}
              }}
              onClick={() => projectCreated && handleStepClick(step.id)}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 3 
                }}>
                  <Box sx={{ 
                    color: step.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: `${step.color}15`,
                    border: `2px solid ${step.color}30`,
                    position: 'relative'
                  }}>
                    {currentStep === step.id ? (
                      <CircularProgress size={24} sx={{ color: step.color }} />
                    ) : completedSteps.includes(step.id) ? (
                      <DoneIcon sx={{ fontSize: 24, color: step.color }} />
                    ) : (
                      step.icon
                    )}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography 
                      variant="h6" 
                      component="h3"
                      sx={{ 
                        fontWeight: 500,
                        color: 'text.primary'
                      }}
                    >
                      {step.title}
                    </Typography>
                    {currentStep === step.id && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Executing step...
                      </Typography>
                    )}
                    {completedSteps.includes(step.id) && (
                      <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                        ✓ Completed
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Status Box */}
        <Alert 
          severity={allStepsComplete ? "success" : projectCreated ? "info" : "warning"}
          icon={allStepsComplete ? <CheckIcon /> : null}
          sx={{
            py: 2,
            px: 3,
            fontWeight: 500,
            borderRadius: 2
          }}
        >
          {allStepsComplete 
            ? "Project Created! All setup steps have been completed successfully." 
            : isCreatingProject
            ? "Setting up project... Please wait while steps are executed automatically."
            : "Please fill out all fields and click 'Create Project' to begin automated setup."}
        </Alert>

        {/* Success Snackbar */}
        <Snackbar
          open={showSuccess}
          autoHideDuration={3000}
          onClose={() => setShowSuccess(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setShowSuccess(false)} severity="success" variant="filled">
            🎉 Project "{project}" created successfully for client "{client}"! All setup steps completed.
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
};

export default ProjectSetup2Page;